import { createFileRoute } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions - WeLivdIt" },
      { name: "description", content: "Terms and conditions for using WeLivdIt visa interview coaching services." },
      { property: "og:title", content: "Terms & Conditions - WeLivdIt" },
      { property: "og:description", content: "The agreement governing use of WeLivdIt's US visa interview coaching, mock interviews, and refusal-support services." },
      { property: "og:url", content: "https://welivdit.com/terms" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <section className="mx-auto max-w-3xl px-5 py-16 md:py-24">
      <p className="text-sm font-medium text-primary uppercase tracking-wider">Legal</p>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight">Terms &amp; Conditions</h1>
      <p className="mt-3 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="mt-10 space-y-8 text-foreground/80 leading-relaxed">
        <div className="rounded-2xl border border-amber-300/40 bg-amber-50 p-5 text-amber-900">
          <p className="font-semibold">Important disclaimers</p>
          <ul className="mt-2 list-disc pl-5 space-y-1 text-sm">
            <li><strong>No visa guarantee.</strong> WeLivdIt does not guarantee a visa approval. Visa decisions are made solely by the consular officer / U.S. Department of State.</li>
            <li><strong>Educational purpose only.</strong> All sessions, materials, and guidance are for educational and preparatory purposes. We are not a law firm, immigration consultancy, or government agent.</li>
            <li><strong>No refund after service access.</strong> Once a coaching session, mock interview, or DS-160 review has been delivered or accessed, fees are non-refundable. See our Refund Policy for full conditions.</li>
          </ul>
        </div>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">1. About us</h2>
          <p className="mt-2">WeLivdIt is a brand operated by <strong>{site.legalName}</strong> (CIN: {site.cin}), a company registered in India with its registered office at {site.address.full}. In these Terms, "WeLivdIt", "we", "us", "our" refer to {site.legalName}. By purchasing or using our services, you ("you", "the student") agree to these Terms.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">2. Services we provide</h2>
          <p className="mt-2">We offer mock visa interviews, DS-160 form review, live question-and-answer coaching, group sessions, and community support. The exact scope depends on the plan you purchase (Basic, Full, or Refusal Recovery). All coaching is delivered live via video call - we do not share documents, question banks, sample answer sheets, or written study materials.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">3. What we do NOT do</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>We do not file, submit, or fill any government form on your behalf.</li>
            <li>We do not provide legal or immigration advice.</li>
            <li>We do not communicate with embassies, consulates, or USCIS on your behalf.</li>
            <li>We cannot influence or predict the outcome of your visa interview.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">4. Payments</h2>
          <p className="mt-2">All payments are processed via Razorpay. Prices listed on the Pricing page are in INR and inclusive of applicable taxes unless stated otherwise. Payment confirms your acceptance of these Terms and our Refund Policy.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">5. Your responsibilities</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>Provide accurate personal, academic, and financial information for prep sessions.</li>
            <li>Attend scheduled sessions on time. Missed sessions without 24-hour notice are forfeited.</li>
            <li>Use materials shared with you for your own preparation only - no resale or redistribution.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">6. Intellectual property</h2>
          <p className="mt-2">All coaching content, frameworks, and session recordings remain the property of WeLivdIt. You receive a personal, non-transferable license to use any session recordings for your own visa preparation. We do not share documents, question banks, sample answer sheets, or written study materials.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">7. Limitation of liability</h2>
          <p className="mt-2">To the maximum extent permitted by law, WeLivdIt's total liability for any claim arising out of our services is limited to the amount you actually paid us for the specific service in question. We are not liable for visa denials, missed flights, lost admissions, deferrals, or any consequential losses.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">8. Changes to these terms</h2>
          <p className="mt-2">We may update these Terms from time to time. The latest version will always be posted on this page with a revised "Last updated" date.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">9. Governing law</h2>
          <p className="mt-2">These Terms are governed by the laws of India. Any disputes will be subject to the exclusive jurisdiction of the courts of Hyderabad, Telangana.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">10. Contact</h2>
          <p className="mt-2">Questions? Email <a href={`mailto:${site.email}`} className="text-primary underline">{site.email}</a> or message us on <a href={site.whatsapp} target="_blank" rel="nofollow noopener noreferrer" className="text-primary underline">WhatsApp</a>.</p>
        </section>
      </div>
    </section>
  );
}
